<template>
  <layout
    title="YouID – Identity verification with verifiable credentials | Youverse"
    :transparent="true"
    class="overflow-x-hidden"
  >
    <highlightSection
      backgroundImage="/storage/images/products/youId/YouID_identity_verification_platform.jpg"
      altImage="YouID Identity Verification Platform"
      title="Fraud-proof ID verification"
      subtitle="Secure and efficient identity verification solution with advanced anti-spoofing technology and compliance with higher industry standards. "
      leftButton="See how it works"
      urlLeftButton="https://6992654.fs1.hubspotusercontent-na1.net/hubfs/6992654/01.%20ONE%20PAGERS/Youverse_YouID.pdf"
      :imageLeft="true"
    />
    <section1
      title="YouID: All-in-one identity verification for every industry"
      imageSrc="/storage/images/products/youId/identity_verification_solutions.png"
      imageAlt="Face Matching API"
    />
    <section2
      title="Secure identity verification with verifiable credentials"
      :elements="[
        {
          title: `Seamless global ID verification`,
          text: `YouID supports 15,000+ documents from 250+ countries and territories, enabling businesses to cater to a diverse range of users. Handle a wide variety of document types, verifying ID from customers around the globe.`,
        },
        {
          title: `Advanced security and anti-spoofing technology`,
          text: `YouID employs cutting-edge measures like passive liveness detection and deepfake resistance. Compliant with NIST and ISO 30107 Levels 1 and 2, it ensures adherence to the highest biometric security standards.`,
        },
        {
          title: `Customizable and privacy-focused compliance`,
          text: `YouID decentralized architecture acts like a secure, personal locker—only the user holds the key, reducing the risk of data breaches. Meanwhile, verifiable credentials enable secure identity proof without unnecessary data exposure, giving users full control over their data.`,
        },
      ]"
    />

    <div class="my-10">
      <cta
        title="Get started"
        text="Discover YouID and learn how it seamlessly integrates with your business."
        buttonText="Book a Demo"
        buttonLink="/book-a-meeting"
        target="_blank"
      />
    </div>

    <section3
      title="See how YouID works in your industry"
      :elements="[
        {
          title: `Banking`,
          text: `Accelerate onboarding, minimize fraud, and automate KYC/AML checks.`,
          seeMoreUrl: `/use-cases/banking`,
          imageSrc: `/storage/images/home/icons/banking.svg`,
          imageAlt: `Banking`,
          imageStyle: `w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20`,
        },
        {
          title: `iGaming`,
          text: `Simplify player verification, boost onboarding and ensure regulatory compliance.`,
          seeMoreUrl: `/use-cases/igaming`,
          imageSrc: `/storage/images/home/icons/igaming.svg`,
          imageAlt: `iGaming`,
          imageStyle: `w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20`,
        },
        {
          title: `Hospitality`,
          text: `Ensure operational efficiency, protect guests’ data, and eliminate check-in friction.`,
          seeMoreUrl: `/use-cases/hospitality`,
          imageSrc: `/storage/images/home/icons/hospitality.svg`,
          imageAlt: `Hospitality`,
          imageStyle: `w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20`,
        },
        {
          title: `Mobility`,
          text: `Onboard customers instantly, ensure robust driver’s verification, and gain your customers trust.`,
          seeMoreUrl: `/use-cases/mobility`,
          imageSrc: `/storage/images/home/icons/mobility.svg`,
          imageAlt: `Mobility`,
          imageStyle: `w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20`,
        },
      ]"
    />

    <textImageSection
      title="Choose your plan"
      text="Choose your plan and unlock the full potential of top-tier face matching."
      imageSrc="/storage/images/products/face/youverse_authentication_pricing.png"
      imageAlt="Biometric Authentication Id Verification"
      buttonText="Pick your plan"
      buttonLink="/pricing"
    />

    <latestPosts
      title="Read our latest articles and resources"
      :tags="['identity verification']"
    />
    <div class="mt-16 lg:mt-28">
      <cta
        title="Start your free trial today"
        text="Explore YouID’s features at no cost and experience the difference firsthand."
        buttonText="Contact Us"
        buttonLink="/company#contactus"
      />
    </div>
  </layout>
</template>

<script>
const layout = () =>
  import(
    /* webpackChunkName: "layout" */ "./../../Components/Layout/layout.vue"
  );

const highlightSection = () =>
  import(
    /* webpackChunkName: "highlightSection" */ "./../../Components/Layout/highlightSection.vue"
  );

const section1 = () =>
  import(
    /* webpackChunkName: "section1" */ "../../Components/Layout/Products/section1.vue"
  );

const section2 = () =>
  import(
    /* webpackChunkName: "section2" */ "../../Components/Layout/Products/section2.vue"
  );

const cta = () =>
  import(/* webpackChunkName: "cta" */ "./../../Components/Layout/cta.vue");

const section3 = () =>
  import(
    /* webpackChunkName: "section3" */ "../../Components/Layout/Products/section3.vue"
  );

const textImageSection = () =>
  import(
    /* webpackChunkName: "textImageSection" */ "../../Components/Layout/textImageSection.vue"
  );

const latestPosts = () =>
  import(
    /* webpackChunkName: "latestPosts" */ "./../../Components/Layout/latestPosts.vue"
  );

export default {
  components: {
    layout,
    highlightSection,
    section1,
    section2,
    cta,
    section3,
    textImageSection,
    latestPosts,
  },
  props: {
    errors: Object,
  },
};
</script>

